import { useTranslation } from 'react-i18next'
import { useSelector, shallowEqual } from 'react-redux'
import { format } from 'date-fns'

import { Button } from '@mui/material'
import {
  useBonuses,
  useBonusFilters,
  useBonusActions,
} from '@/views/bonus-engine/hooks'
import { dateTimeTableFormat } from '@/constants/date'

import { bonusEngineSelector } from '@/state/bonus-engine/slices/index.slices'
import { BonusLibraryColumnProps } from '@/state/bonus-engine/models'
import { TableGenericComponent } from '@/views/components/table'

import useHasAccess from '@/hooks/use-access.hook'
import { RoleType } from '@/enums'
import { BonusesRowComponent } from './row.component'

export const BonusesTableComponent = () => {
  const { t } = useTranslation()

  const {
    isLoading,
    sortedRows,
    order,
    orderBy,
    headCells,
    pagination,
    createSortHandler,
    handleEditBonus,
    handleDeleteBonus,
    getXlsFormattedRow,
    handleDuplicateBonus,
  } = useBonuses()

  const { hasAccess } = useHasAccess()

  const hasAction = !hasAccess(RoleType.AGENT)

  const { handleChangePage, handleChangeLimit } = useBonusFilters()

  const { getAllBonuses } = useBonusActions()

  const { filters } = useSelector(bonusEngineSelector, shallowEqual)

  const body = sortedRows.map((row: BonusLibraryColumnProps, index: number) => {
    return (
      <BonusesRowComponent
        key={row._id}
        row={row}
        handleDeleteBonus={handleDeleteBonus}
        handleEditBonus={handleEditBonus}
        handleDuplicateBonus={handleDuplicateBonus}
        hasAction={hasAction}
      />
    )
  })

  const fileName = `bonus-programs-${format(new Date(), dateTimeTableFormat)}`

  const xlsxButton = (
    <Button
      onClick={() =>
        getAllBonuses(
          {
            ...filters,
            page: 1,
            limit: 999999,
            xlsx: true,
          },
          headCells,
          fileName,
          getXlsFormattedRow,
        )
      }
      variant="contained"
    >
      {t('labels.downloadAsXLS')}
    </Button>
  )

  return (
    <TableGenericComponent<BonusLibraryColumnProps>
      order={order}
      orderBy={orderBy}
      headCells={headCells}
      rowLength={sortedRows.length}
      pagination={pagination}
      isLoading={isLoading}
      body={body}
      hasAction={hasAction}
      leftContent={xlsxButton}
      createSortHandler={createSortHandler}
      handleChangePage={handleChangePage}
      handleChangeLimit={handleChangeLimit}
    />
  )
}
